import { Component, DestroyRef, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../../../environments/environment';
import { BoardService } from '../../../../../services/board.service';
import { NotificationService } from '../../../../../services/notifications.service';
import {
  CARD_REMINDER_STATUS,
  CardReminderXData,
  CardTimelineEventAction,
  CardTimelineReminderEvent,
} from '../../../board-v3.types';
import { formatFullName } from '../../../../../helpers';

@Component({
  selector: 'app-board-v3-timeline-reminder',
  templateUrl: './timeline-reminder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3TimelineReminderComponent {
  @Input() cardId!: string;
  @Input() event!: CardTimelineReminderEvent;
  /** Repeat and edit fill the card reminder form, so they are hidden where there is no such form */
  @Input() canPrefill = true;
  @Output() statusToggled = new EventEmitter<CardTimelineReminderEvent>();
  @Output() repeat = new EventEmitter<CardReminderXData>();
  @Output() edit = new EventEmitter<CardReminderXData>();

  private readonly boardService = inject(BoardService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly CARD_REMINDER_STATUS = CARD_REMINDER_STATUS;
  updatingStatus = false;

  get isFired(): boolean {
    return this.event?.action === CardTimelineEventAction.FIRED;
  }

  get isOverdue(): boolean {
    if (this.isFired) return false;

    const xData = this.event?.x_data;
    if (!xData || xData.status !== CARD_REMINDER_STATUS.PENDING) return false;

    const dueAt = new Date(xData.due_at).getTime();
    return !Number.isNaN(dueAt) && dueAt < Date.now();
  }

  toggleDone(): void {
    if (!this.event?.x_data) return;

    const nextStatus =
      this.event.x_data.status === CARD_REMINDER_STATUS.DONE ? CARD_REMINDER_STATUS.PENDING : CARD_REMINDER_STATUS.DONE;

    this.updateStatus(nextStatus);
  }

  onCancelClick(): void {
    this.updateStatus(CARD_REMINDER_STATUS.CANCELLED);
  }

  onRepeatClick(): void {
    if (!this.event?.x_data) return;

    this.repeat.emit(this.event.x_data);
  }

  onEditClick(): void {
    if (!this.event?.x_data) return;

    this.edit.emit(this.event.x_data);
  }

  private updateStatus(nextStatus: CARD_REMINDER_STATUS): void {
    if (!this.event?.x_data || this.updatingStatus) return;

    const previousStatus = this.event.x_data.status;
    this.event.x_data.status = nextStatus;
    this.updatingStatus = true;

    this.boardService
      .updateCardReminderStatus(this.cardId, this.event.x_data.card_reminder_id, nextStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updatingStatus = false;
          this.statusToggled.emit(this.event);
        },
        error: () => {
          this.event.x_data.status = previousStatus;
          this.updatingStatus = false;
          this.notificationService.showError('', 'Failed to update reminder status');
        },
      });
  }

  getAuthorName(): string {
    const user = this.event?.created_by_user;
    if (!user) return 'System';

    return formatFullName(user.first_name, user.last_name) || user.email || 'System';
  }

  getAuthorAvatarUrl(): string | undefined {
    return this.event?.created_by_user?.avatar_url || undefined;
  }
}
